import { constantCase } from 'change-case';
import type { FullSubEvent, SubEventSegment } from '@fastnorth/timer-api';
import { SEGMENT_FIELDS } from '../../consts.ts';
import resolveSegmentField from '../../utils/resolveSegmentField.ts';
import type { ResultDetail } from '../../../../../../../../graphql/rest-link/types.ts';
import type { SubstitutionMap } from './types.ts';

type BuildSegmentSubstitutionsParams = {
  result: ResultDetail['result'];
  subEvent: FullSubEvent;
  content: string;
};

const buildSegmentSubstitutions = ({
  result,
  subEvent,
  content,
}: BuildSegmentSubstitutionsParams): SubstitutionMap => {
  if (!result.segments || result.segments.length === 0 || !subEvent.columns) {
    return {};
  }

  const segments = subEvent.segments || [];

  // Index segments by id and group them by type (in order of appearance) for label derivation
  const subEventSegmentMap = segments.reduce<Record<string, SubEventSegment>>((acc, segment) => {
    acc[segment.id] = segment;
    return acc;
  }, {});

  const segmentsByType = segments.reduce<Record<string, SubEventSegment[]>>((acc, segment) => {
    const arr = acc[segment.type.key] ?? [];
    arr.push(segment);
    acc[segment.type.key] = arr;
    return acc;
  }, {});

  const substitutions: SubstitutionMap = {};
  const segmentColumns = subEvent.columns.filter((column) => column.type === 'segment');

  segmentColumns.forEach((segmentColumn) => {
    const { segmentId } = segmentColumn;

    const segmentResult = result.segments.find((segment) => segment.segmentId === segmentId);
    const segmentConfig = subEventSegmentMap[segmentId];

    if (!segmentResult || !segmentConfig) {
      return;
    }

    const type = segmentConfig.type.key;
    const segmentIndex = segmentsByType[type].findIndex((segment) => segment.id === segmentId) + 1;
    const label = `${type}_${segmentIndex}`;
    const segmentTemplatePrefix = `%${label.toUpperCase()}`;

    if (!content.includes(segmentTemplatePrefix)) {
      return;
    }

    SEGMENT_FIELDS.forEach((field) => {
      const templateKey = `${segmentTemplatePrefix}_${constantCase(field, { mergeAmbiguousCharacters: true }).toUpperCase()}%`;

      if (content.includes(templateKey)) {
        const attrName = resolveSegmentField(field);
        const altAttrName = field.charAt(0).toLowerCase() + field.slice(1);

        substitutions[templateKey] = segmentResult[attrName] || segmentResult[altAttrName] || '';
      }
    });

    const genericSegmentKey = `${segmentTemplatePrefix}%`;
    if (content.includes(genericSegmentKey)) {
      substitutions[genericSegmentKey] = segmentResult.time || segmentResult.Time || '';
    }
  });

  return substitutions;
};

export default buildSegmentSubstitutions;
